import { STEPS } from '../constants/steps';

/**
 * Handles price calculations for sport trading cards
 */
export default class PriceCalculator {
    /**
     * Create a new PriceCalculator
     * 
     * @param {object} options - Pricing options
     */
    constructor(options = {}) {
        this.options = {
            basePrice: 29.99,
            ...options
        };
        
        this.lastCalculation = {
            basePrice: this.options.basePrice,
            frontExtras: 0,
            backExtras: 0,
            totalExtras: 0,
            total: this.options.basePrice
        };
        
        // Reference to step manager (will be set by Designer)
        this.stepManager = null;
    }
    
    /**
     * Set the step manager reference
     * 
     * @param {object} stepManager - StepManager instance
     */
    setStepManager(stepManager) {
        this.stepManager = stepManager;
    }
    
    /**
     * Set base price
     * 
     * @param {number} price - New base price
     */
    setBasePrice(price) {
        this.options.basePrice = price;
        this.lastCalculation.basePrice = price;
        this.lastCalculation.total = price + this.lastCalculation.totalExtras;
    }
    
    /**
     * Get a step handler from the step controller via step manager
     * 
     * @param {string} side - 'front' or 'back'
     * @param {string} stepId - Step ID
     * @returns {object|null} Step handler or null if not found
     */
    getStepHandler(side, stepId) {
        if (!this.stepManager || !this.stepManager.stepController) {
            return null;
        }
        
        return this.stepManager.stepController.getHandlerForSide(side, stepId);
    }
    
    /**
     * Calculate price based on completed steps and step data
     * 
     * @param {object} stepsData - Data from all steps on both sides
     * @param {object} completedSteps - Completed steps on both sides
     * @returns {object} Price breakdown
     */
    calculatePrice(stepsData, completedSteps) {
        let frontExtras = 0;
        let backExtras = 0;
        
        // Calculate front side extras
        STEPS.front.forEach(step => {
            if (completedSteps.front.includes(step.id)) {
                const stepData = stepsData.front[step.id];
                
                // Check if we have a handler for this step
                const handler = this.getStepHandler('front', step.id);
                if (handler && typeof handler.getPrice === 'function') {
                    // Use handler's getPrice method
                    frontExtras += handler.getPrice();
                } else {
                    // Fall back to step definition price if enabled
                    if (step.price && (!stepData || !('enabled' in stepData) || stepData.enabled)) {
                        frontExtras += step.price;
                    }
                }
            }
        });
        
        // Calculate back side extras
        STEPS.back.forEach(step => {
            if (completedSteps.back.includes(step.id)) {
                const stepData = stepsData.back[step.id];
                
                // Check if we have a handler for this step
                const handler = this.getStepHandler('back', step.id);
                if (handler && typeof handler.getPrice === 'function') {
                    // Use handler's getPrice method
                    backExtras += handler.getPrice();
                } else {
                    // Fall back to step definition price if enabled
                    if (step.price && (!stepData || !('enabled' in stepData) || stepData.enabled)) {
                        backExtras += step.price;
                    }
                }
            }
        });
        
        const totalExtras = frontExtras + backExtras;
        const total = this.options.basePrice + totalExtras;
        
        // Store last calculation
        this.lastCalculation = {
            basePrice: this.options.basePrice,
            frontExtras,
            backExtras,
            totalExtras,
            total
        };
        
        return this.lastCalculation;
    }
    
    /**
     * Get the most recent price calculation
     * 
     * @returns {object} Price breakdown
     */
    getLastCalculation() {
        return this.lastCalculation;
    }
    
    /**
     * Format price for display
     * 
     * @param {number} price - Price to format
     * @param {object} options - Formatting options
     * @returns {string} Formatted price
     */
    static formatPrice(price, options = {}) {
        const defaults = {
            prefix: '$',
            decimals: 2,
            showPlus: false
        };
        
        const settings = { ...defaults, ...options };
        
        const prefix = price > 0 && settings.showPlus ? '+' : '';
        return `${prefix}${settings.prefix}${Math.abs(price).toFixed(settings.decimals)}`;
    }
    
    /**
     * Generate an itemized price breakdown
     * 
     * @param {object} calculation - Price calculation
     * @returns {Array} Array of line items with labels and prices
     */
    static getItemizedBreakdown(calculation) {
        const items = [
            {
                label: 'Base Price',
                amount: calculation.basePrice,
                type: 'base'
            }
        ];
        
        // Add front extras if any
        if (calculation.frontExtras > 0) {
            items.push({
                label: 'Front Side Extras',
                amount: calculation.frontExtras,
                type: 'extras',
                side: 'front'
            });
        }
        
        // Add back extras if any
        if (calculation.backExtras > 0) {
            items.push({
                label: 'Back Side Extras',
                amount: calculation.backExtras,
                type: 'extras',
                side: 'back'
            });
        }
        
        // Add total
        items.push({
            label: 'Total Price',
            amount: calculation.total,
            type: 'total'
        });
        
        return items;
    }
}